import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ToastService } from '../toast/toast.service';

import { DataTableCellDirective } from './data-table-cell.directive';
import { DataTableExpandDirective } from './data-table-expand.directive';
import { DataTableHeaderCellDirective } from './data-table-header-cell.directive';

import { skeletonColumnCount } from '../skeleton/skeleton.util';

import {

  formatDataTableCell,

  type DataTableColumn,

  type DataTableColumnFilters,

  type DataTableRowContextMenuEvent,

  type DataTableRowInteractionEvent,

  type DataTableSortChange,

  type DataTableSortOrder,

} from './data-table.types';

import {
  DATA_TABLE_DEFAULT_TABLE_CLASS,
  buildDataTableResizeStorageKey,
  clampDataTableColumnWidthPx,
  dataTableColumnFitClass,
  dataTableHeaderLabelTitle,
  dataTableColumnStructureEquals,
  dataTableColumnTrackWidths,
  dataTableManualTableSizing,
  dataTableWrapperClass,
  isDataTableColumnResizable,
  loadStoredDataTableColumnWidths,
  measureDataTableEndTracks,
  resolveDataTableClass,
  saveStoredDataTableColumnWidths,
  type DataTableLayout,
} from './data-table-display.util';

import {
  applyDataTableColumnFilterValue,
  collectDataTableDistinctFilterValues,
  dataTableShowColumnFilterEmpty,
  dataTableShowEmptyFiltered,
  dataTableShowTableBlock,
  filterDataTableRows,
  hasActiveDataTableColumnFilters,
  isDataTableColumnFilterable,
  isDataTableFilterPointerOutside,
  resolveDataTableColumnFilterMode,
} from './data-table-filter.util';

import {
  isDataTableColumnSortable,
  sliceDataTablePage,
  sortDataTableRows,
} from './data-table-sort.util';

import { filterOverlayPanelStyle } from '../../../utils/filter-overlay-position.util';
import { createBasicAoAStyleSheet, downloadExcelJsAoAWorkbook } from '../../../utils/xlsx-workbook.util';
import { buildDataTableExportAoA, dataTableExportColumns } from './data-table-export.util';
import { dataTableExtraSummary } from './data-table-summary.util';
import { PageHeaderTableToolsService } from '../../../../core/services/page-header-table-tools.service';



export type DataTableDensity = 'default' | 'compact' | 'tight';

export type DataTableVariant = 'surface' | 'bordered' | 'plain';
export type { DataTableLayout };

/** Delay between consecutive row enters after skeleton → content (ms). */
const ROW_STAGGER_STEP_MS = 40;
/** Indices beyond this share the same delay so long tables finish quickly. */
const ROW_STAGGER_MAX_INDEX = 19;
/** Matches `--tt-duration-row` in `_motion.scss`. */
const ROW_STAGGER_DURATION_MS = 220;



@Component({

  selector: 'app-data-table',

  templateUrl: './data-table.component.html',

})

export class DataTableComponent<T = unknown> implements AfterContentInit, AfterViewInit, OnChanges, OnDestroy {
  private readonly toast = inject(ToastService);


  @ContentChildren(DataTableCellDirective, { descendants: true }) cellTemplates!: QueryList<DataTableCellDirective>;
  @ContentChildren(DataTableHeaderCellDirective, { descendants: true }) headerTemplates!: QueryList<DataTableHeaderCellDirective>;
  @ContentChild(DataTableExpandDirective) expandTemplate?: DataTableExpandDirective;

  @ViewChild('columnFilterPanel') private columnFilterPanelEl?: ElementRef<HTMLElement>;
  @ViewChild('toolbarActions') private toolbarActions?: ElementRef<HTMLElement>;

  /**
   * True while the list is wider than the card, so the table scrolls inside
   * itself. Starts on so the first paint cannot spill onto the page; cleared
   * once the columns fit, which keeps the sticky header pinned to `<main>`.
   */
  horizontalScroll = true;

  /** Measured actions / select / expand column widths. Empty until the first pass. */
  private endTrackPx: Readonly<Record<string, number>> = {};
  private endTrackMeasureTimer?: ReturnType<typeof setTimeout>;

  /** User-adjusted column widths (`columnId` → width in px). */
  manualColumnWidths: Record<string, number> = {};
  /** Column IDs directly dragged by the user (distinguishes explicit vs implicitly frozen left columns). */
  private userResizedColumnIds = new Set<string>();
  private loadedResizeStorageKey: string | null = null;
  resizingColumnId: string | null = null;
  private activeResizeCleanup: (() => void) | null = null;

  private observedScrollEl: HTMLElement | null = null;
  private tableResizeObserver?: ResizeObserver;
  private resizeObserverRaf: number | null = null;

  @ViewChild('tableScroll')
  set tableScrollRef(ref: ElementRef<HTMLElement> | undefined) {
    this.bindTableScrollObserver(ref?.nativeElement ?? null);
  }

  private readonly tableTools = inject(PageHeaderTableToolsService);
  private readonly zone = inject(NgZone);



  @Input() columns: DataTableColumn<T>[] = [];

  @Input() rows: T[] = [];



  @Input() sortBy = '';

  @Input() sortOrder: DataTableSortOrder = 'asc';

  @Output() sortChange = new EventEmitter<DataTableSortChange>();

  /**
   * When true (default), the table sorts `rows` client-side for display.
   * Set `false` when the parent already sorts (custom comparator) or uses server sort.
   */
  @Input() clientSort = true;

  /**
   * When true, `rows` is the full filtered set — the table filters, sorts, then slices the current page.
   * Use with in-memory lists that previously passed a pre-sliced `paginated*` array.
   */
  @Input() clientPipeline = false;

  /**
   * When true (default), the table applies per-column header filters to `rows`.
   * With `clientPipeline`, filters run on the full list before sort/paginate.
   * Without it (e.g. server-paginated objects), filters only affect the current page of rows.
   */
  @Input() clientFilter = true;

  /**
   * Column-header filter panel opens above the funnel.
   * Floor-apartment tables sit low on the object detail; opening down covers the rows.
   */
  @Input() columnFilterPreferAbove = false;

  /**
   * List pages park the toolbar on the page-header subtitle.
   * Nested tables keep `[dataTableFilters]` directly above the table.
   */
  @Input() parkToolbarInHeader = true;

  /** Optional controlled filter map; when set, parent owns filter state. */
  @Input() columnFilters: DataTableColumnFilters = {};

  @Output() columnFiltersChange = new EventEmitter<DataTableColumnFilters>();



  @Input() currentPage = 1;

  @Input() itemsPerPage = 100;

  /** Celkový počet po filtrech (stránkování). */

  @Input() totalItems = 0;

  @Output() pageChange = new EventEmitter<number>();



  /** Počet v datasetu před filtry — pro prázdný stav „žádný výsledek filtru“. */

  @Input() sourceTotal = 0;

  @Input() summaryItemLabel = 'položek';

  /** Vlastní text místo „Zobrazeno X z Y …“. */

  @Input() summaryOverride?: string;



  @Input() loading = false;

  @Input() loadingMessage = 'Načítání…';

  @Input() errorMessage = '';

  @Output() retry = new EventEmitter<void>();



  @Input() emptyTitle = 'Žádná data';

  @Input() emptyMessage = 'Nejsou k dispozici žádné záznamy.';

  @Input() emptyFilteredMessage = 'Žádné záznamy neodpovídají filtrům.';

  /**
   * @deprecated Ignored — `app-data-table` always uses the shared `app-empty-state` default SVG
   * (`EMPTY_STATE_ICON_PATH`). Kept so existing `[emptyIcon]` bindings compile without churn.
   */
  @Input() emptyIcon = '';



  @Input() clickableRows = true;

  /** When set, only rows for which this returns true are clickable (overrides global `clickableRows` per row). */
  @Input() rowClickableFn?: (row: T) => boolean;

  /** Optional accessible name for clickable rows (screen readers announce "row, button, <label>" instead of just "button"). */
  @Input() rowAriaLabelFn?: (row: T) => string;

  @Input() enablePagination = true;

  /** Akce „Zobrazit všechny řádky“ pod spodním stránkováním (vyžaduje handler u rodiče). */
  @Input() enableShowAll = true;
  /**
   * Hide „Zobrazit všechny řádky“ when `totalItems` exceeds this (0 = no cap).
   * Large desk lists (e.g. Objekty ~168k) must stay paginated — materializing the
   * full set into Angular rows freezes the UI.
   */
  @Input() maxShowAllItems = 0;
  @Input() showAllRowsActive = false;
  @Input() showAllLoading = false;
  @Output() showAll = new EventEmitter<void>();
  @Output() exitShowAll = new EventEmitter<void>();

  @Input() showSummary = true;

  /** Labeled Excel export of the filtered rows and visible column headers. */
  @Input() enableExport = true;

  /** Parent is already writing a workbook (specialized export). */
  @Input() exporting = false;

  /** File name prefix. The date stamp is appended. */
  @Input() exportFileName = 'export';

  /**
   * When a parent listens, it owns the download (full server result, styled workbook).
   * Otherwise the table exports the filtered rows it already has.
   */
  @Output() exportRequested = new EventEmitter<void>();

  exportBusy = false;

  @Input() paginationWrapperClass?: string;



  @Input() density: DataTableDensity = 'default';

  @Input() variant: DataTableVariant = 'surface';

  @Input() tableClass = DATA_TABLE_DEFAULT_TABLE_CLASS;

  /**
   * `fluid` — identity (`tt-col--primary`) absorbs leftover width;
   * compact (`tt-col--compact`) / metric (`tt-col--metric`) stay fixed-narrow.
   * On narrow viewports they keep min-widths and scroll horizontally.
   */
  @Input() layout: DataTableLayout = 'default';

  @Input() tableMinWidth?: string;

  /** Enables manual column width resizing via drag handles on `<th>` edges. */
  @Input() enableColumnResize = true;

  /** Persists manual column widths in `localStorage` per route + column signature. */
  @Input() persistColumnWidths = true;

  /** Optional explicit storage key scope for persisted column widths. */
  @Input() columnResizeStorageKey?: string;

  // neutral-200 (light warm gray) / neutral-100 (dark mode = #111827 deep bg) — correct for inverted neutral scale
  @Input() theadClass = 'bg-neutral-200 dark:bg-neutral-100';

  @Input() rowBaseClass = '';

  @Input() rowClassFn?: (row: T) => string;

  @Input() trackByFn?: (index: number, row: T) => unknown;

  /**
   * Stabilní id řádku pro `data-list-row-id` (obnova scroll pozice po návratu z detailu).
   */
  @Input() rowDomIdFn?: (row: T) => string | null | undefined;

  /** Volitelné — s `appDataTableExpand` vykreslí pod řádkem detail. */
  @Input() rowExpandedFn?: (row: T) => boolean;

  @Output() rowClick = new EventEmitter<T>();

  /**
   * Same as `rowClick`, but includes the pointer/keyboard event (Shift/Ctrl multi-select).
   * Prefer this when modifiers or coordinates matter; keep `rowClick` for simple navigation.
   */
  @Output() rowActivate = new EventEmitter<DataTableRowInteractionEvent<T>>();

  /** Double-click (or Enter) — e.g. open detail without using single-click for navigation. */
  @Output() rowDblClick = new EventEmitter<DataTableRowInteractionEvent<T>>();

  /** Pravý klik na řádek — rodič může otevřít vlastní kontextové menu. */
  @Output() rowContextMenu = new EventEmitter<DataTableRowContextMenuEvent<T>>();

  /** Volitelné — např. zvýraznění dávky trasy ve výkazu práce při najetí myší. */
  @Output() rowEnter = new EventEmitter<T>();

  @Output() rowLeave = new EventEmitter<T>();

  /**
   * Vnořený *ngFor sloupců v řádcích se jinak neobnoví, pokud se `rows` vykreslí dřív než `columns`
   * (prázdné buňky, ale existující `<tr>`). Při změně sloupců invalidujeme trackBy řádků.
   */
  private rowRenderEpoch = 0;

  private templateByColumnId = new Map<string, DataTableCellDirective>();
  private headerTemplateByColumnId = new Map<string, DataTableHeaderCellDirective>();

  /**
   * When true, data rows use `tt-table-row-enter` with staggered delays.
   * Armed on loading exit (and empty→rows while idle); cleared after the max stagger window
   * so filter keystrokes / in-place row updates do not re-animate.
   */
  rowStaggerArmed = false;

  private rowStaggerClearTimer: ReturnType<typeof setTimeout> | null = null;

  /** Active sort — updated on header click; synced from `@Input() sortBy` / `sortOrder`. */
  activeSortBy = '';
  activeSortOrder: DataTableSortOrder = 'asc';

  /** Active column filters — synced from `@Input() columnFilters` when the parent binds them. */
  activeColumnFilters: DataTableColumnFilters = {};

  /** Column id whose filter popover is open (one at a time). */
  openFilterColumnId: string | null = null;
  filterPanelStyle: { [key: string]: string } = {};

  private filterTriggerEl: HTMLElement | null = null;
  /** Stable panel node ref (survives body portal; ViewChild alone can go stale). */
  private filterPanelNode: HTMLElement | null = null;
  private filterPanelInBody = false;
  private filterPanelOriginalParent: Node | null = null;
  private filterDismissUnlisten: (() => void) | null = null;
  private filterDismissAttachTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly repositionFilterPanel = (): void => this.updateFilterPanelPosition();
  private readonly onFilterDocumentPointerDown = (event: Event): void => {
    this.onDocumentPointerDownForFilter(event);
  };
  private readonly onFilterDocumentKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeColumnFilter();
    }
  };

  ngAfterContentInit(): void {
    this.rebuildTemplateMap();
    this.cellTemplates.changes.subscribe(() => this.rebuildTemplateMap());
    this.rebuildHeaderTemplateMap();
    this.headerTemplates.changes.subscribe(() => this.rebuildHeaderTemplateMap());
  }

  ngAfterViewInit(): void {
    const el = this.toolbarActions?.nativeElement;
    if (el && this.parkToolbarInHeader) this.tableTools.attach(el);
  }

  ngOnDestroy(): void {
    this.teardownActiveResize();
    if (this.endTrackMeasureTimer != null) {
      clearTimeout(this.endTrackMeasureTimer);
      this.endTrackMeasureTimer = undefined;
    }
    this.tableResizeObserver?.disconnect();
    this.tableResizeObserver = undefined;
    if (this.resizeObserverRaf != null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.resizeObserverRaf);
      this.resizeObserverRaf = null;
    }
    this.observedScrollEl = null;
    const el = this.toolbarActions?.nativeElement;
    if (el) this.tableTools.detach(el);
    this.clearRowStaggerTimer();
    this.teardownFilterPanelListeners();
    this.detachFilterDismissListeners();
    if (this.filterPanelInBody) {
      (this.filterPanelNode ?? this.columnFilterPanelEl?.nativeElement)?.remove();
      this.filterPanelInBody = false;
      this.filterPanelOriginalParent = null;
      this.filterPanelNode = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sortBy']) {
      this.activeSortBy = this.sortBy ?? '';
    }
    if (changes['sortOrder']) {
      this.activeSortOrder = this.sortOrder ?? 'asc';
    }
    if (changes['columnFilters']) {
      this.activeColumnFilters = { ...(this.columnFilters ?? {}) };
    }

    let bump = false;
    let columnsStructureChanged = false;

    if (changes['columns']) {
      const prev = changes['columns'].previousValue as DataTableColumn<T>[] | undefined;
      const curr = this.columns ?? [];
      columnsStructureChanged = !dataTableColumnStructureEquals(prev, curr);
      // Only invalidate row trackBy when column *ids* change — reference-only churn from
      // parent getters must not destroy 100 DOM rows on every CD cycle.
      if (columnsStructureChanged && this.rows.length > 0) {
        bump = true;
      }
    }

    if (changes['rows']) {
      const prevLen = (changes['rows'].previousValue as T[] | undefined)?.length ?? 0;
      const currLen = this.rows?.length ?? 0;
      if (prevLen === 0 && currLen > 0 && (this.columns?.length ?? 0) > 0) {
        bump = true;
      }
    }

    if (bump) {
      this.rowRenderEpoch++;
    }

    // Stagger only on skeleton → content, or when rows first appear while not loading
    // (hub refresh / initial bind). Skip routine filter/page swaps that keep rows non-empty.
    if (changes['loading']) {
      const prevLoading = changes['loading'].previousValue;
      const currLoading = changes['loading'].currentValue;
      if (prevLoading === true && currLoading === false) {
        this.armRowStagger();
      }
    }

    if (changes['rows'] && !this.loading) {
      const prevLen = (changes['rows'].previousValue as T[] | undefined)?.length ?? 0;
      const currLen = this.rows?.length ?? 0;
      if (prevLen === 0 && currLen > 0) {
        this.armRowStagger();
      }
    }

    if (
      columnsStructureChanged ||
      changes['columnResizeStorageKey'] ||
      changes['persistColumnWidths']
    ) {
      this.syncStoredColumnWidths();
    }

    if (
      changes['rows'] ||
      columnsStructureChanged ||
      changes['layout'] ||
      changes['loading']
    ) {
      this.scheduleEndTrackMeasure();
    }
  }

  /** Show-all control — disabled for huge server-paginated totals. */
  get showAllActionEnabled(): boolean {
    if (!this.enableShowAll) return false;
    if (this.showAllRowsActive) return true;
    const cap = this.maxShowAllItems;
    if (cap > 0 && this.effectiveTotalItems > cap) return false;
    return true;
  }

  /** CSS `animation-delay` for staggered row enter (capped). */
  rowEnterDelay(index: number): string {
    const capped = Math.min(Math.max(index, 0), ROW_STAGGER_MAX_INDEX);
    return `${capped * ROW_STAGGER_STEP_MS}ms`;
  }

  private armRowStagger(): void {
    this.rowStaggerArmed = true;
    this.clearRowStaggerTimer();
    const maxDelayMs = ROW_STAGGER_MAX_INDEX * ROW_STAGGER_STEP_MS;
    this.rowStaggerClearTimer = setTimeout(() => {
      this.rowStaggerArmed = false;
      this.rowStaggerClearTimer = null;
    }, maxDelayMs + ROW_STAGGER_DURATION_MS + 80);
  }

  private clearRowStaggerTimer(): void {
    if (this.rowStaggerClearTimer != null) {
      clearTimeout(this.rowStaggerClearTimer);
      this.rowStaggerClearTimer = null;
    }
  }



  readonly skeletonRowCount = 8;

  get skeletonColumnCount(): number {
    return skeletonColumnCount(this.columns?.length);
  }

  /**
   * Keep the table (and column-filter popover host) mounted whenever the parent
   * still has rows. Column filters that match nothing must not tear down thead
   * or the body-portaled panel — that left filters stuck with an empty state.
   */
  get showTableBlock(): boolean {
    return dataTableShowTableBlock({
      loading: this.loading,
      errorMessage: this.errorMessage,
      hasInputRows: this.hasInputRows,
    });
  }



  get showEmptySource(): boolean {
    return !this.loading && !this.errorMessage && !this.hasInputRows && this.sourceTotal === 0;
  }



  /** Parent-level filters emptied `rows` (page search etc.), not column filters. */
  get showEmptyFiltered(): boolean {
    return dataTableShowEmptyFiltered({
      loading: this.loading,
      errorMessage: this.errorMessage,
      hasInputRows: this.hasInputRows,
      sourceTotal: this.sourceTotal,
    });
  }

  /** Column filters active and no row matches — show inside the table, keep headers. */
  get showColumnFilterEmpty(): boolean {
    return dataTableShowColumnFilterEmpty({
      hasInputRows: this.hasInputRows,
      hasPipelineRows: this.hasPipelineRows,
      hasActiveColumnFilters: this.hasActiveColumnFilters,
    });
  }



  get displayedCount(): number {

    return this.displayRows.length;

  }



  /** Raw input rows (before column filters). */
  get hasInputRows(): boolean {
    return (this.rows?.length ?? 0) > 0;
  }

  /**
   * Rows after column filters (and before page slice). Used for empty states / totals.
   */
  get filteredRows(): T[] {
    const list = this.rows ?? [];
    if (!this.clientFilter) return list;
    return filterDataTableRows(list, this.columns ?? [], this.activeColumnFilters);
  }

  get hasPipelineRows(): boolean {
    return this.filteredRows.length > 0;
  }

  /** @deprecated Prefer `hasPipelineRows` — kept as alias for template/callers. */
  get hasRows(): boolean {
    return this.hasPipelineRows;
  }

  /**
   * Pagination / summary total: with `clientPipeline`, use filtered length so column filters
   * shrink the pager; otherwise keep the parent-provided `totalItems` (server pagination).
   */
  get effectiveTotalItems(): number {
    if (this.clientPipeline) {
      return this.filteredRows.length;
    }
    return this.totalItems;
  }

  get hasActiveColumnFilters(): boolean {
    return hasActiveDataTableColumnFilters(this.activeColumnFilters);
  }

  /**
   * Rows rendered in the body — optionally filtered, client-sorted, and (with `clientPipeline`) page-sliced.
   */
  get displayRows(): T[] {
    let list: T[] = this.filteredRows;
    if (this.clientSort && this.activeSortBy) {
      list = sortDataTableRows(list, this.columns ?? [], this.activeSortBy, this.activeSortOrder);
    }
    // Slice even when built-in pager is hidden (parent owns `app-table-pagination`).
    if (this.clientPipeline && !this.showAllRowsActive) {
      list = sliceDataTablePage(list, this.currentPage, this.itemsPerPage, false);
    }
    return list;
  }

  isColumnSortable(column: DataTableColumn<T>): boolean {
    return isDataTableColumnSortable(column);
  }

  isColumnFilterable(column: DataTableColumn<T>): boolean {
    return this.clientFilter && isDataTableColumnFilterable(column);
  }

  isColumnFilterActive(column: DataTableColumn<T>): boolean {
    return !!(this.activeColumnFilters[column.id] ?? '').trim();
  }

  get openFilterColumn(): DataTableColumn<T> | null {
    if (!this.openFilterColumnId) return null;
    return (this.columns ?? []).find((c) => c.id === this.openFilterColumnId) ?? null;
  }

  columnFilterMode(column: DataTableColumn<T>): 'text' | 'select' | 'number' | 'date' {
    return resolveDataTableColumnFilterMode(column, this.rows ?? []);
  }

  columnFilterOptions(column: DataTableColumn<T>): string[] {
    return collectDataTableDistinctFilterValues(this.rows ?? [], column);
  }

  columnFilterValue(columnId: string): string {
    return this.activeColumnFilters[columnId] ?? '';
  }

  columnFilterPlaceholder(column: DataTableColumn<T>): string {
    const mode = this.columnFilterMode(column);
    if (mode === 'number') return 'např. 10–20';
    if (mode === 'date') return 'např. 1.1.2024';
    return 'Filtrovat…';
  }

  filterButtonAriaLabel(column: DataTableColumn<T>): string {
    const active = this.isColumnFilterActive(column);
    if (active) {
      return `Filtr sloupce ${column.label}: ${this.columnFilterValue(column.id)}. Upravit filtr.`;
    }
    return `Filtrovat sloupec ${column.label}`;
  }

  toggleColumnFilter(column: DataTableColumn<T>, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isColumnFilterable(column)) return;
    if (this.openFilterColumnId === column.id) {
      this.closeColumnFilter();
      return;
    }
    // Close any open panel first so body portal / listeners stay consistent.
    if (this.openFilterColumnId) {
      this.closeColumnFilter();
    }
    this.openFilterColumnId = column.id;
    this.filterTriggerEl = event.currentTarget as HTMLElement;
    this.updateFilterPanelPosition();
    window.addEventListener('scroll', this.repositionFilterPanel, true);
    window.addEventListener('resize', this.repositionFilterPanel);
    requestAnimationFrame(() => {
      this.moveFilterPanelToBody();
      this.filterPanelNode = this.columnFilterPanelEl?.nativeElement ?? this.filterPanelNode;
      this.updateFilterPanelPosition();
      this.focusFilterPanelControl();
      this.attachFilterDismissListeners();
    });
  }

  closeColumnFilter(): void {
    if (!this.openFilterColumnId && !this.filterPanelInBody) return;
    this.detachFilterDismissListeners();
    this.restoreFilterPanelToHost();
    this.openFilterColumnId = null;
    this.filterTriggerEl = null;
    this.filterPanelNode = null;
    this.teardownFilterPanelListeners();
  }

  onColumnFilterInput(column: DataTableColumn<T>, value: string): void {
    if (!this.isColumnFilterable(column)) return;
    const next = applyDataTableColumnFilterValue(this.activeColumnFilters, column.id, value);
    this.activeColumnFilters = next;
    this.columnFiltersChange.emit({ ...next });
    if (this.clientPipeline && this.currentPage !== 1) {
      this.pageChange.emit(1);
    }
  }

  clearColumnFilter(column: DataTableColumn<T>, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.onColumnFilterInput(column, '');
    this.closeColumnFilter();
  }

  onFilterPanelPointerDown(event: MouseEvent): void {
    event.stopPropagation();
  }

  /** @internal — document mousedown (capture). */
  onDocumentPointerDownForFilter(event: Event): void {
    if (!this.openFilterColumnId) return;
    const panel = this.filterPanelNode ?? this.columnFilterPanelEl?.nativeElement ?? null;
    if (
      !isDataTableFilterPointerOutside(event.target, this.filterTriggerEl, panel)
    ) {
      return;
    }
    this.closeColumnFilter();
  }

  private updateFilterPanelPosition(): void {
    const btn = this.filterTriggerEl;
    if (!btn) return;
    const panel = this.filterPanelNode ?? this.columnFilterPanelEl?.nativeElement;
    this.filterPanelStyle = filterOverlayPanelStyle(btn, panel, 220, {
      estimatedHeightPx: 140,
      preferAbove: this.columnFilterPreferAbove,
    });
  }

  private moveFilterPanelToBody(): void {
    const panel = this.columnFilterPanelEl?.nativeElement;
    if (!panel || this.filterPanelInBody) return;
    this.filterPanelOriginalParent = panel.parentNode;
    document.body.appendChild(panel);
    this.filterPanelInBody = true;
    this.filterPanelNode = panel;
  }

  private restoreFilterPanelToHost(): void {
    if (!this.filterPanelInBody) return;
    const panel = this.filterPanelNode ?? this.columnFilterPanelEl?.nativeElement;
    if (panel && this.filterPanelOriginalParent) {
      this.filterPanelOriginalParent.appendChild(panel);
    } else {
      // Orphan safety: panel was portaled then view torn down — drop it from body.
      panel?.remove();
    }
    this.filterPanelInBody = false;
    this.filterPanelOriginalParent = null;
  }

  private focusFilterPanelControl(): void {
    const panel = this.filterPanelNode ?? this.columnFilterPanelEl?.nativeElement;
    if (!panel) return;
    const el = panel.querySelector<HTMLElement>('input, select');
    el?.focus();
  }

  /**
   * Attach dismiss listeners after the opening gesture finishes.
   * Capture-phase `mousedown` + Escape; panel may live under `document.body`.
   */
  private attachFilterDismissListeners(): void {
    this.detachFilterDismissListeners();
    // Skip the same-tick opening click so the popover does not close immediately.
    this.filterDismissAttachTimer = setTimeout(() => {
      this.filterDismissAttachTimer = null;
      if (!this.openFilterColumnId) return;
      document.addEventListener('mousedown', this.onFilterDocumentPointerDown, true);
      document.addEventListener('keydown', this.onFilterDocumentKeydown, true);
      this.filterDismissUnlisten = () => {
        document.removeEventListener('mousedown', this.onFilterDocumentPointerDown, true);
        document.removeEventListener('keydown', this.onFilterDocumentKeydown, true);
      };
    }, 0);
  }

  private detachFilterDismissListeners(): void {
    if (this.filterDismissAttachTimer != null) {
      clearTimeout(this.filterDismissAttachTimer);
      this.filterDismissAttachTimer = null;
    }
    this.filterDismissUnlisten?.();
    this.filterDismissUnlisten = null;
  }

  private teardownFilterPanelListeners(): void {
    window.removeEventListener('scroll', this.repositionFilterPanel, true);
    window.removeEventListener('resize', this.repositionFilterPanel);
  }



  /** Non-count total (meters, Kč, a sum) lifted out of `summaryOverride`. */
  get extraSummaryText(): string {
    return dataTableExtraSummary(this.summaryOverride);
  }

  get summaryText(): string {
    if (this.extraSummaryText) return this.extraSummaryText;
    if (this.summaryOverride?.trim()) return this.summaryOverride.trim();
    return `Zobrazeno ${this.displayedCount} z ${this.effectiveTotalItems} ${this.summaryItemLabel}`;
  }

  get showToolbarSummary(): boolean {
    if (this.loading || !!this.errorMessage || !this.showSummary) return false;
    if (this.extraSummaryText) return true;
    if (!(this.hasInputRows || !!this.summaryOverride?.trim())) return false;
    // Pager already shows „řádky a–b z c“.
    if (this.showPaginationBar && this.effectiveTotalItems > 0) return false;
    return true;
  }

  get showPaginationBar(): boolean {
    return !this.loading && !this.errorMessage && (this.enablePagination || this.showAllRowsActive) && this.hasRows;
  }

  /** Filtered rows in the current sort, including rows on other pages. */
  get rowsForExport(): T[] {
    let list = this.filteredRows;
    if (this.clientSort && this.activeSortBy) {
      list = sortDataTableRows(list, this.columns ?? [], this.activeSortBy, this.activeSortOrder);
    }
    return list;
  }

  get exportButtonDisabled(): boolean {
    if (this.loading || !!this.errorMessage || this.exporting || this.exportBusy) return true;
    if (this.exportRequested.observed) {
      return this.effectiveTotalItems <= 0 && !this.hasInputRows;
    }
    return this.rowsForExport.length === 0 || dataTableExportColumns(this.columns ?? []).length === 0;
  }

  onExportClick(): void {
    if (this.exportButtonDisabled) return;
    if (this.exportRequested.observed) {
      this.exportRequested.emit();
      return;
    }
    void this.exportVisibleRows();
  }

  private async exportVisibleRows(): Promise<void> {
    const rows = this.rowsForExport;
    const aoa = buildDataTableExportAoA(this.columns ?? [], rows);
    if (aoa.length <= 1) return;
    this.exportBusy = true;
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      const base = (this.exportFileName || 'export').replace(/\.xlsx$/i, '');
      await downloadExcelJsAoAWorkbook(`${base}-${stamp}.xlsx`, [
        { name: 'Export', aoa, styleSheet: createBasicAoAStyleSheet() },
      ]);
    } catch (error) {
      console.error('Table Excel export failed', error);
      this.toast.showError('Nepodařilo se exportovat tabulku do Excelu.');
    } finally {
      this.exportBusy = false;
    }
  }



  trackRow(index: number, row: T): unknown {
    const base = this.trackByFn ? this.trackByFn(index, row) : index;
    return `${this.rowRenderEpoch}:${base}`;
  }

  rowDomIdAttr(row: T): string | null {
    const raw = this.rowDomIdFn?.(row);
    const id = raw == null ? '' : String(raw).trim();
    return id || null;
  }

  /** Stabilní klíč sloupce — bez trackBy by nové pole `columns` z getteru zničilo buňky mezi mousedown a click (checkboxy). */
  trackColumn(_index: number, column: DataTableColumn<T>): string {

    return column.id;

  }



  isRowClickable(row: T): boolean {
    if (this.rowClickableFn) return this.rowClickableFn(row);
    return this.clickableRows;
  }

  rowClasses(row: T): string {

    const parts: string[] = [];

    if (this.rowBaseClass) parts.push(this.rowBaseClass);

    if (this.isRowClickable(row)) {
      // Hover/active via shared .tt-table-row--clickable (see styles/components/_tables.scss).
      parts.push('tt-table-row--clickable');
    }

    const extra = this.rowClassFn?.(row);

    if (extra) parts.push(extra);

    return parts.join(' ').trim();

  }



  onHeaderClick(column: DataTableColumn<T>): void {

    if (!this.isColumnSortable(column)) return;

    const next: DataTableSortChange =

      this.activeSortBy === column.id

        ? { sortBy: column.id, sortOrder: this.activeSortOrder === 'asc' ? 'desc' : 'asc' }

        : { sortBy: column.id, sortOrder: 'asc' };

    this.activeSortBy = next.sortBy;
    this.activeSortOrder = next.sortOrder;
    this.sortChange.emit(next);

  }



  /** Full column name for the clipped header letters. Not on the sort or filter controls. */
  headerLabelTitle(column: DataTableColumn<T>): string | null {
    return dataTableHeaderLabelTitle(column);
  }

  /** Accessible name for the sort button — announces column + current/next direction (screen readers). */
  sortButtonAriaLabel(column: DataTableColumn<T>): string {
    if (this.activeSortBy !== column.id) {
      return `Seřadit podle: ${column.label}`;
    }
    const current = this.activeSortOrder === 'asc' ? 'vzestupně' : 'sestupně';
    const next = this.activeSortOrder === 'asc' ? 'sestupně' : 'vzestupně';
    return `${column.label}, seřazeno ${current}. Přepnout na řazení ${next}.`;
  }



  onRowClick(event: MouseEvent, row: T): void {

    if (!this.isRowClickable(row)) return;

    this.rowClick.emit(row);
    this.rowActivate.emit({ row, event });

  }

  onRowDblClick(event: MouseEvent, row: T): void {
    if (!this.isRowClickable(row)) return;
    this.rowDblClick.emit({ row, event });
  }

  onRowContextMenu(event: MouseEvent, row: T): void {
    this.rowContextMenu.emit({ row, event });
  }

  isRowExpanded(row: T): boolean {
    return !!this.expandTemplate && !!this.rowExpandedFn?.(row);
  }



  onRowKeydown(event: KeyboardEvent, row: T): void {

    if (!this.isRowClickable(row)) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      // Prefer double-click semantics (open detail) while keeping legacy `rowClick` consumers.
      this.rowDblClick.emit({ row, event });
      this.rowClick.emit(row);
      this.rowActivate.emit({ row, event });
      return;
    }

    if (event.key === ' ') {
      event.preventDefault();
      this.rowClick.emit(row);
      this.rowActivate.emit({ row, event });
    }

  }



  rowAriaLabel(row: T): string | null {
    if (!this.isRowClickable(row) || !this.rowAriaLabelFn) return null;
    return this.rowAriaLabelFn(row);
  }

  cellTemplate(columnId: string): DataTableCellDirective | undefined {

    return this.templateByColumnId.get(columnId);

  }



  headerTemplate(columnId: string): DataTableHeaderCellDirective | undefined {

    return this.headerTemplateByColumnId.get(columnId);

  }



  formatCell(column: DataTableColumn<T>, row: T): string {
    let raw: unknown;
    if (column.format) {
      raw = column.format(row);
    } else if (row != null && typeof row === 'object' && column.id in (row as object)) {
      raw = (row as Record<string, unknown>)[column.id];
    } else {
      raw = '';
    }
    return formatDataTableCell(raw, column.emptyText ?? '—');
  }

  /** Full cell text for the native tooltip when a single line (or a 2-line clamp) is cut. */
  cellTitle(column: DataTableColumn<T>, row: T): string | null {
    const explicit = column.title?.(row)?.trim();
    if (explicit) return explicit;
    if (column.cell === 'template' && this.cellTemplate(column.id)) return null;
    const text = this.formatCell(column, row).trim();
    const empty = (column.emptyText ?? '—').trim();
    if (!text || text === empty) return null;
    return text;
  }



  thClass(column: DataTableColumn<T>): string {

    const align = column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left';

    const sortable = this.isColumnSortable(column) ? 'cursor-pointer' : '';

    const nowrap = column.nowrap !== false ? 'whitespace-nowrap' : '';

    return `${this.thPadding()} ${align} ${sortable} ${nowrap} ${dataTableColumnFitClass(column, this.layout)} ${column.headerClass ?? ''}`.trim();

  }

  /** Merges `tableClass` with optional fluid layout modifier. */
  resolvedTableClass(): string {
    return resolveDataTableClass(this.tableClass, this.layout);
  }

  get hasManualColumnWidths(): boolean {
    return Object.keys(this.manualColumnWidths).length > 0;
  }

  isColumnResizable(column: DataTableColumn<T>): boolean {
    return this.enableColumnResize && isDataTableColumnResizable(column);
  }

  isColumnManuallySized(column: DataTableColumn<T>): boolean {
    return this.userResizedColumnIds.has(column.id) && this.manualColumnWidths[column.id] != null;
  }

  manualColumnWidthPx(column: DataTableColumn<T>): number | null {
    const px = this.manualColumnWidths[column.id];
    return px != null && px > 0 ? px : null;
  }

  trackColumnIndex(index: number): number {
    return index;
  }

  /**
   * Fixed column tracks for a default list (or fluid list once manually resized).
   */
  columnTrackWidths(): string[] {
    if (this.layout === 'fluid' && !this.hasManualColumnWidths) return [];
    return dataTableColumnTrackWidths(this.columns, this.endTrackPx, this.manualColumnWidths);
  }

  resolvedTableMinWidth(): string | null {
    // Fluid matrices keep their own CSS tracks / ≤48rem max-content; only lock a
    // floor when the user has resized columns (or for default list layout).
    if (this.layout === 'fluid' && !this.hasManualColumnWidths) {
      return this.tableMinWidth || null;
    }
    const { minWidthPx } = dataTableManualTableSizing(
      this.columns,
      this.endTrackPx,
      this.manualColumnWidths,
    );
    if (minWidthPx != null && minWidthPx > 0) {
      return `${minWidthPx}px`;
    }
    return this.tableMinWidth || null;
  }

  resolvedTableWidth(): string | null {
    const { explicitWidthPx } = dataTableManualTableSizing(
      this.columns,
      this.endTrackPx,
      this.manualColumnWidths,
    );
    if (explicitWidthPx != null && explicitWidthPx > 0) {
      return `${explicitWidthPx}px`;
    }
    return null;
  }

  onColumnResizeStart(event: MouseEvent, column: DataTableColumn<T>, colIndex: number): void {
    if (event.button !== 0 || !this.isColumnResizable(column)) return;
    event.preventDefault();
    event.stopPropagation();
    this.teardownActiveResize();

    const handleEl = event.currentTarget as HTMLElement | null;
    const table = (this.observedScrollEl?.querySelector('table') ??
      handleEl?.closest('table')) as HTMLTableElement | null;
    if (!table) return;

    const thEls = Array.from(table.querySelectorAll('thead > tr > th')) as HTMLElement[];
    const targetTh = thEls[colIndex];
    const currentWidth =
      targetTh?.getBoundingClientRect().width || this.manualColumnWidths[column.id] || 120;
    const startWidth = clampDataTableColumnWidthPx(currentWidth);
    const startX = event.clientX;

    // Lock resizable columns to the left of the dragged column at their current rendered width
    // so the dragged column's left edge stays stationary and its right edge tracks the cursor 1:1.
    const nextManual: Record<string, number> = { ...this.manualColumnWidths };
    for (let i = 0; i < colIndex; i++) {
      const leftCol = this.columns[i];
      if (!leftCol || !this.isColumnResizable(leftCol)) continue;
      if (nextManual[leftCol.id] == null) {
        const w = thEls[i]?.getBoundingClientRect().width;
        if (w && w > 0) {
          nextManual[leftCol.id] = clampDataTableColumnWidthPx(w);
        }
      }
    }
    nextManual[column.id] = startWidth;
    this.manualColumnWidths = nextManual;
    this.userResizedColumnIds.add(column.id);
    this.resizingColumnId = column.id;

    table.classList.add('tt-table--resizing');
    handleEl?.classList.add('tt-col-resizer--active');
    if (typeof document !== 'undefined') {
      document.body.classList.add('tt-col-resizing');
    }

    this.applyManualWidthsToDom(table);

    this.zone.runOutsideAngular(() => {
      const onMouseMove = (moveEvent: MouseEvent): void => {
        const deltaX = moveEvent.clientX - startX;
        const nextWidth = clampDataTableColumnWidthPx(startWidth + deltaX);
        if (this.manualColumnWidths[column.id] === nextWidth) return;
        this.manualColumnWidths = {
          ...this.manualColumnWidths,
          [column.id]: nextWidth,
        };
        this.applyManualWidthsToDom(table);
      };

      const onMouseUp = (): void => {
        this.teardownActiveResize();
        this.persistManualColumnWidths();
        this.zone.run(() => {
          this.resizingColumnId = null;
          this.syncHorizontalScroll();
        });
      };

      window.addEventListener('mousemove', onMouseMove, { passive: true });
      window.addEventListener('mouseup', onMouseUp);

      this.activeResizeCleanup = () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
        table.classList.remove('tt-table--resizing');
        handleEl?.classList.remove('tt-col-resizer--active');
        if (typeof document !== 'undefined') {
          document.body.classList.remove('tt-col-resizing');
        }
      };
    });
  }

  onColumnResizeReset(event: MouseEvent, column: DataTableColumn<T>): void {
    event.preventDefault();
    event.stopPropagation();
    this.teardownActiveResize();
    this.resizingColumnId = null;

    if (event.shiftKey || !this.manualColumnWidths[column.id]) {
      this.manualColumnWidths = {};
      this.userResizedColumnIds.clear();
    } else {
      this.userResizedColumnIds.delete(column.id);
      const next = { ...this.manualColumnWidths };
      delete next[column.id];
      if (this.userResizedColumnIds.size === 0) {
        this.manualColumnWidths = {};
      } else {
        this.manualColumnWidths = next;
      }
    }

    this.persistManualColumnWidths();
    const table = this.observedScrollEl?.querySelector('table');
    if (table instanceof HTMLTableElement) {
      this.applyManualWidthsToDom(table);
    }
    this.measureEndTracks();
    this.syncHorizontalScroll();
  }

  private applyManualWidthsToDom(table: HTMLTableElement): void {
    const hasManual = this.hasManualColumnWidths;
    const tracks =
      this.layout === 'fluid' && !hasManual
        ? []
        : dataTableColumnTrackWidths(this.columns, this.endTrackPx, this.manualColumnWidths);
    const { minWidthPx, explicitWidthPx } = dataTableManualTableSizing(
      this.columns,
      this.endTrackPx,
      this.manualColumnWidths,
    );

    let colgroup = table.querySelector('colgroup');
    if (!colgroup && tracks.length > 0) {
      colgroup = document.createElement('colgroup');
      for (let i = 0; i < tracks.length; i++) {
        colgroup.appendChild(document.createElement('col'));
      }
      table.insertBefore(colgroup, table.firstChild);
    }
    if (colgroup) {
      if (tracks.length === 0) {
        colgroup.remove();
      } else {
        const cols = Array.from(colgroup.querySelectorAll('col')) as HTMLElement[];
        for (let i = 0; i < tracks.length; i++) {
          if (cols[i]) {
            cols[i].style.width = tracks[i];
          }
        }
      }
    }

    const thEls = Array.from(table.querySelectorAll('thead > tr > th')) as HTMLElement[];
    this.columns.forEach((col, idx) => {
      const px = this.manualColumnWidths[col.id];
      const val = px != null && px > 0 ? `${px}px` : '';
      const th = thEls[idx];
      if (th) {
        th.style.width = val;
        th.style.minWidth = val;
        th.style.maxWidth = val;
      }
    });

    for (const row of Array.from(table.tBodies[0]?.rows ?? [])) {
      if (row.cells.length !== this.columns.length) continue;
      this.columns.forEach((col, idx) => {
        const px = this.manualColumnWidths[col.id];
        const val = px != null && px > 0 ? `${px}px` : '';
        const td = row.cells[idx];
        if (td) {
          td.style.width = val;
          td.style.minWidth = val;
          td.style.maxWidth = val;
        }
      });
    }

    const applyFloor = this.layout !== 'fluid' || hasManual;
    table.style.minWidth =
      applyFloor && minWidthPx != null && minWidthPx > 0
        ? `${minWidthPx}px`
        : this.tableMinWidth || '';
    table.style.width = explicitWidthPx != null && explicitWidthPx > 0 ? `${explicitWidthPx}px` : '';

    const scrollEl = this.observedScrollEl;
    if (scrollEl) {
      const needsScroll = table.scrollWidth > this.availableTableWidth(scrollEl) + 2;
      scrollEl.classList.toggle('tt-data-table__scroll--x', needsScroll);
      this.horizontalScroll = needsScroll;
    }
  }

  private teardownActiveResize(): void {
    this.activeResizeCleanup?.();
    this.activeResizeCleanup = null;
  }

  private resolveResizeStorageKey(): string | null {
    if (!this.persistColumnWidths) return null;
    const pathname = typeof window !== 'undefined' ? window.location?.pathname || '/' : '/';
    return buildDataTableResizeStorageKey(this.columns ?? [], this.columnResizeStorageKey, pathname);
  }

  private syncStoredColumnWidths(): void {
    if (!this.persistColumnWidths) {
      this.loadedResizeStorageKey = null;
      return;
    }
    const key = this.resolveResizeStorageKey();
    if (key === this.loadedResizeStorageKey) return;
    this.loadedResizeStorageKey = key;
    const loaded = loadStoredDataTableColumnWidths(key, this.columns ?? []);
    this.manualColumnWidths = loaded.manualColumnWidths;
    this.userResizedColumnIds = loaded.userResizedColumnIds;
  }

  private persistManualColumnWidths(): void {
    if (!this.persistColumnWidths) return;
    const key = this.resolveResizeStorageKey();
    saveStoredDataTableColumnWidths(key, this.manualColumnWidths, this.userResizedColumnIds);
  }



  tdClass(column: DataTableColumn<T>): string {

    const align = column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left';

    const nowrap = column.nowrap !== false ? 'whitespace-nowrap' : '';

    const tone = column.cellClass ?? 'text-sm text-neutral-900 dark:text-neutral-100';

    return `${this.tdPadding()} ${align} ${nowrap} ${dataTableColumnFitClass(column, this.layout)} ${tone}`.trim();

  }



  tableWrapperClass(): string {
    return dataTableWrapperClass(this.variant);
  }



  private thPadding(): string {

    switch (this.density) {

      case 'compact':

        return 'px-2 py-1.5';

      case 'tight':

        return 'px-1.5 py-1';

      default:

        return 'px-3 py-2';

    }

  }



  private tdPadding(): string {

    switch (this.density) {

      case 'compact':

        return 'px-2 py-1.5';

      case 'tight':

        return 'px-1.5 py-1';

      default:

        return 'px-3 py-2';

    }

  }



  private bindTableScrollObserver(el: HTMLElement | null): void {
    if (el === this.observedScrollEl && this.tableResizeObserver) return;
    this.tableResizeObserver?.disconnect();
    this.tableResizeObserver = undefined;
    if (this.resizeObserverRaf != null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.resizeObserverRaf);
      this.resizeObserverRaf = null;
    }
    this.observedScrollEl = el;
    if (!el || typeof ResizeObserver === 'undefined') {
      this.horizontalScroll = false;
      return;
    }
    // Coalesce layout thrash: unstable column widths / scroll mode must not
    // schedule measure+zone.run on every observer tick.
    const observer = new ResizeObserver(() => {
      if (this.resizingColumnId) return;
      if (this.resizeObserverRaf != null) return;
      this.resizeObserverRaf = requestAnimationFrame(() => {
        this.resizeObserverRaf = null;
        this.measureEndTracks();
        this.syncHorizontalScroll();
      });
    });
    this.tableResizeObserver = observer;
    this.zone.runOutsideAngular(() => {
      observer.observe(el);
      const table = el.querySelector('table');
      if (table) observer.observe(table);
    });
    requestAnimationFrame(() => {
      this.measureEndTracks();
      this.syncHorizontalScroll();
    });
  }

  /** Re-measure after the new row buttons are in the DOM. */
  private scheduleEndTrackMeasure(): void {
    if (this.layout === 'fluid' || this.endTrackMeasureTimer != null) return;
    this.endTrackMeasureTimer = setTimeout(() => {
      this.endTrackMeasureTimer = undefined;
      this.measureEndTracks();
    }, 0);
  }

  /**
   * Size actions / select / expand to their buttons so the fixed tracks
   * reserve only that width and the data columns share the rest.
   */
  private measureEndTracks(): void {
    if (this.layout === 'fluid') return;
    const table = this.observedScrollEl?.querySelector('table');
    if (!(table instanceof HTMLTableElement)) return;
    const measured = measureDataTableEndTracks(table, this.columns);
    const ids = Object.keys(measured);
    if (ids.length === 0) return;
    let changed = false;
    for (const id of ids) {
      const prev = this.endTrackPx[id];
      if (prev == null || Math.abs(prev - measured[id]) > 1) changed = true;
    }
    if (!changed) return;
    this.zone.run(() => {
      this.endTrackPx = { ...this.endTrackPx, ...measured };
    });
  }

  /**
   * Width the columns must fit into. If the scroller has already grown to the
   * table (iPad first layout, before overflow containment), compare against the
   * page instead of that inflated clientWidth — otherwise the pan class turns
   * off and the filter row is laid out past the clip until a rotation.
   */
  private availableTableWidth(el: HTMLElement): number {
    const own = el.clientWidth;
    const page = el.closest('main.app-page-scroll');
    const pageWidth = page instanceof HTMLElement ? page.clientWidth : 0;
    if (pageWidth > 0 && (own <= 0 || own > pageWidth)) return pageWidth;
    return own;
  }

  /**
   * Scroll inside the table only when columns still exceed the card.
   * Uses content-floor min-width (not crushed scrollWidth) and hysteresis so a
   * scrollbar appearing/disappearing cannot flip fit↔overflow every frame.
   */
  private syncHorizontalScroll(): void {
    const el = this.observedScrollEl;
    if (!el) return;
    const table = el.querySelector('table');
    const useFloor = this.layout !== 'fluid' || this.hasManualColumnWidths;
    const { minWidthPx } = useFloor
      ? dataTableManualTableSizing(this.columns, this.endTrackPx, this.manualColumnWidths)
      : { minWidthPx: null };
    const measured = table ? table.scrollWidth : el.scrollWidth;
    const needed = Math.max(measured, minWidthPx ?? 0);
    const client = this.availableTableWidth(el);
    // Typical classic scrollbar thickness — stay in scroll mode until content
    // clearly fits even after the gutter/scrollbar space returns.
    const hysteresisPx = 16;
    const next = this.horizontalScroll
      ? needed > client - hysteresisPx + 2
      : needed > client + 2;
    if (next === this.horizontalScroll) return;
    this.zone.run(() => {
      this.horizontalScroll = next;
    });
  }

  private rebuildTemplateMap(): void {

    this.templateByColumnId.clear();

    for (const dir of this.cellTemplates ?? []) {

      if (dir.columnId) {

        this.templateByColumnId.set(dir.columnId, dir);

      }

    }

  }



  private rebuildHeaderTemplateMap(): void {

    this.headerTemplateByColumnId.clear();

    for (const dir of this.headerTemplates ?? []) {

      if (dir.columnId) {

        this.headerTemplateByColumnId.set(dir.columnId, dir);

      }

    }

  }

}


